import { startOfTomorrow } from "date-fns";
import { JwtClient } from "utils/jwtClient";

export type GetContractsProps = {
  asAtDate?: string;
  customerNumber: string;
  jwtClient: JwtClient;
  supplyNodeRef: string;
};

export type ContractsPromise = {
  contractName?: string;
  description?: string;
  endsAt?: string;
  id?: string;
  name?: string;
};

export const getContracts = async ({
  customerNumber,
  supplyNodeRef,
  jwtClient,
  asAtDate = new Date().toISOString(),
}: GetContractsProps): Promise<ContractsPromise> => {
  const response = await jwtClient.apiCall({
    path: "/contract_manager/contracts",
    queryParams: {
      party_ref: `/customer/customers/${customerNumber}`,
      supply_node_ref: supplyNodeRef,
      as_at: asAtDate,
    },
  });
  const { data, included: contractInformation } = await response.json();
  const endsAt = data[0].attributes?.period?.end_at;

  const currentProduct = Array.isArray(contractInformation)
    ? contractInformation.find((element) => element.type === "product")
        ?.attributes
    : undefined;

  const currentContract = Array.isArray(contractInformation)
    ? contractInformation.find((element) => element.type === "plan")
    : undefined;

  const currentContractsAttributes = currentContract?.attributes;

  return {
    contractName: currentContractsAttributes?.name,
    description: currentProduct?.description,
    ...(endsAt && { endsAt }),
    id: currentContract?.id,
    name: currentProduct?.name,
  };
};

export const getPlans = async ({
  customerNumber,
  supplyNodeRef,
  jwtClient,
}: GetContractsProps): Promise<{ planId: string; description: string }[]> => {
  const response = await jwtClient.apiCall({
    path: "/contract_manager/plans",
    queryParams: {
      as_at: new Date().toISOString(),
      brand: "flick",
      existing_customer_plans: true,
      party_ref: `/customer/customers/${customerNumber}`,
      supply_node_ref: supplyNodeRef,
    },
  });

  const { data, included: productInformation } = await response.json();

  const planDescriptions = data.map((plan: Contract) => {
    const productId = plan.relationships?.product?.data.id;
    if (productId) {
      return {
        planId: plan.id,
        description: productInformation.find(
          (product: Contract) => product.id === productId,
        )?.attributes?.description,
      };
    }
  });

  return planDescriptions;
};

type UpdateCurrentPlanProps = {
  customerNumber: string;
  jwtClient: JwtClient;
  planId: string;
  supplyNodeRef: string;
};

export const updateCurrentPlan = async ({
  customerNumber,
  jwtClient,
  planId,
  supplyNodeRef,
}: UpdateCurrentPlanProps) => {
  const queryParams = {
    party_ref: `/customer/customers/${customerNumber}`,
    plan_id: planId,
    start_at: startOfTomorrow().toISOString(),
    supply_node_ref: supplyNodeRef,
  };
  const response = await jwtClient.apiCall({
    path: "/contract_manager/create_contracts",
    queryParams,
    fetchParams: {
      method: "POST",
    },
  });

  const { included } = await response.json();
  const contract = Array.isArray(included)
    ? included.find((element) => element.type === "plan")
    : undefined;

  return { planId: contract?.id };
};
